<div>
    <!-- Page Header -->
    <app-page-header title="Dashboard" subtitle="Overview of sales, reservations, and performance metrics">
        <div actions class="flex gap-3">
            <p-button
                label="Show Reports"
                icon="expand_more"
                [outlined]="true"
                styleClass="p-button-secondary"
                (click)="menu.toggle($event)"
            />
            <p-menu #menu [model]="reportButtonItems" [popup]="true" />

            <p-date-picker
                #mainDatePicker
                [(ngModel)]="rangeDates"
                selectionMode="range"
                [readonlyInput]="true"
                [showIcon]="true"
                [firstDayOfWeek]="1"
                [numberOfMonths]="2"
                [showOnFocus]="true"
                [hideOnDateTimeSelect]="false"
                [dateFormat]="'dd.mm.yy'"
                [inputStyleClass]="'font-semibold bg-gray-50'"
                [panelStyleClass]="'custom-datepicker-panel'"
                [inputStyle]="{ width: '240px' }"
            >
                <ng-template pTemplate="header">
                    <div class="custom-datepicker-header">
                        <div class="date-range-list">
                            <div
                                *ngFor="let range of dateRanges"
                                [class.selected]="selectedDateRange() === range.value"
                                class="date-range-item"
                                (click)="setDateRange(range.value)"
                            >
                                {{ range.label }}
                            </div>
                        </div>
                    </div>
                </ng-template>
                <ng-template pTemplate="footer">
                    <div class="custom-datepicker-footer flex justify-between mt-3">
                        <div class="pt-2">
                            <p-checkbox
                                [(ngModel)]="flightDateBased"
                                [binary]="true"
                                inputId="flightDateBased"
                                (onChange)="defineReportButtonItems()"
                            />
                            <label for="flightDateBased" class="ml-2 text-sm text-gray-700">Search Flight Date Based</label>
                        </div>
                        <div class="flex gap-2">
                            <p-button label="Cancel" [outlined]="true" (click)="cancel()" />
                            <p-button label="Apply" (click)="apply()" />
                        </div>
                    </div>
                </ng-template>
            </p-date-picker>
        </div>
    </app-page-header>

    <!-- Content -->
    <section class="grid grid-cols-12 gap-4 mt-4">
        <!-- Left Container - Stats Cards -->
        <div class="col-span-4 flex flex-col gap-3 left-container justify-between">
            <!-- Seat Sale Information -->
            <app-card icon="group" title="Seat Sales" iconGradient="from-blue-500 to-blue-600">
                <div class="grid grid-cols-3 gap-2 mt-2">
                    <!-- Adult -->
                    <div class="flex flex-col items-center p-2 rounded-lg bg-rose-50 border border-rose-100">
                        <span class="material-icons-outlined text-rose-600 mb-1">person</span>
                        <span class="text-xs text-rose-700 font-medium">Adult</span>
                        <span class="text-lg font-bold text-rose-800">{{ salesInformationData()?.adultSeat }}</span>
                    </div>
                    <!-- Child -->
                    <div class="flex flex-col items-center p-2 rounded-lg bg-blue-50 border border-blue-100">
                        <span class="material-icons-outlined text-blue-600 mb-1">favorite</span>
                        <span class="text-xs text-blue-700 font-medium">Child</span>
                        <span class="text-lg font-bold text-blue-800">{{ salesInformationData()?.childSeat }}</span>
                    </div>
                    <!-- Infant -->
                    <div class="flex flex-col items-center p-2 rounded-lg bg-emerald-50 border border-emerald-100">
                        <span class="material-icons-outlined text-emerald-600 mb-1">star</span>
                        <span class="text-xs text-emerald-700 font-medium">Infant</span>
                        <span class="text-lg font-bold text-emerald-800">{{ salesInformationData()?.infantSeat }}</span>
                    </div>
                </div>
            </app-card>

            <!-- Cancelled -->
            <app-card icon="cancel" title="Cancelled" iconGradient="from-red-500 to-red-600">
                <div class="flex items-center justify-end -mt-6">
                    <span class="text-2xl font-bold text-gray-900">{{ salesInformationData()?.cancelled }}</span>
                </div>
            </app-card>

            <!-- Today's Revenue -->
            <app-card icon="payments" title="Today's Revenue" iconGradient="from-green-500 to-emerald-600">
                <div class="flex items-center justify-end -mt-6">
                    <span class="text-2xl font-bold text-green-600">{{
                        salesInformationData()?.revenue?.amount | currency: salesInformationData()?.revenue?.currency
                    }}</span>
                </div>
            </app-card>

            <!-- Total Reservation -->
            <app-card icon="confirmation_number" title="Total Reservations" iconGradient="from-purple-500 to-indigo-600">
                <div class="flex items-center justify-end -mt-6">
                    <span class="text-2xl font-bold text-gray-900">{{ totalReservationValue() }}</span>
                </div>
            </app-card>
        </div>

        <!-- Agent Information Table -->
        <app-agent-information class="col-span-8" [agentInformationData]="agentInformationData()?.results" [loading]="loading()" />

        <!-- Best Seller Route Table -->
        <app-best-seller-route class="col-span-12" [bestSellerRouteData]="bestSellerRouteData()?.results" [loading]="loading()" />

        <!-- Agent Reservation Cancel & Change Table -->
        <app-agent-reservation-cancel-change
            class="col-span-6"
            [agentBiStatisticData]="agentBiStatisticsData()?.results"
            [loading]="loading()"
        />

        <!-- Airline Statistics Table -->
        <app-airline-statistics class="col-span-6" [airlineStatisticsData]="airlineStatisticsData()?.results" [loading]="loading()" />

        <!-- Doughnut Chart -->
        <app-card class="col-span-4" icon="pie_chart" title="All Sales Breakdown" iconGradient="from-teal-500 to-green-600">
            <app-doughnut-chart [salesChannelData]="salesChannelData()?.total" [loading]="loading()" />
        </app-card>

        <!-- Bar Chart -->
        <app-card class="col-span-8" icon="bar_chart" title="Monthly Sales Channel" iconGradient="from-violet-500 to-purple-600">
            <app-stacked-bar-chart [salesChannelMonthlyData]="salesChannelData()?.monthly" [loading]="loading()" />
        </app-card>

        <!-- Basic Chart -->
        <app-card class="col-span-12" icon="trending_up" title="Provider Sales Performance" iconGradient="from-amber-500 to-orange-600">
            <app-basic-chart [providerInformationData]="providerInformationData()?.salesNumber" [loading]="loading()" />
        </app-card>
    </section>
</div>
